import React from 'react'
// libs
import { Route, Routes } from 'react-router'
// components
import ProtectedRoute from 'components/ProtectedRoute'
// auth pages
import LoginPage from 'pages/login/index'
import Verify2FA from 'pages/login/verify-2fa'
import AzureSSO from 'pages/azuresso/index'
import Callback from 'pages/callback'
import ResetPassword from 'pages/reset-password/index'
import ResetPasswordConfirm from 'pages/reset-password/confirm'
// main pages
import HomePage from 'pages/home/index'
import AccountSecurity from 'pages/account/security'
// plans
import PlansPage from 'pages/plans/[[...slug]]'
import PlanEditPage from 'pages/plans/[companyId]/edit/[id]'
// objectives / kpis / actions
import KpisPage from 'pages/kpis/[[...slug]]'
import ActionsPage from 'pages/actions/[[...slug]]'
import ObjectivesPage from 'pages/objectives/[[...slug]]'
// forms
import FormsPage from 'pages/forms/index'
import FormsEditPage from 'pages/forms/edit/[id]/template/[templateId]'
import FormsCreatePage from 'pages/forms/create/[id]'
import FormsEditMultiplePage from 'pages/forms/templates/edit/[id]'
import FormsEditTemplatePage from 'pages/forms/edit/template/[templateId]'
// public forms
import PublicFormPage from 'pages/public/form/[id]'
import PublicFormCreatePage from 'pages/public/form/create/[id]'
// data hub
import DataHubPage from 'pages/data-hub/index'
import DataHubReportPage from 'pages/data-hub/report/[id]/workspace/[workspaceId]/name/[name]'
import DataHubReportNewPage from 'pages/data-hub/report/new/dataset/[datasetId]/workspace/[workspaceId]'
import DataHubReportEditPage from 'pages/data-hub/report/edit/[id]/workspace/[workspaceId]/name/[name]'
// settings
import SettingsAccountsPage from 'pages/settings/accounts/index'
import SettingsAccountsNewPage from 'pages/settings/accounts/new'
import SettingsAccountsEditPage from 'pages/settings/accounts/[id]'
import SettingsAiUsagePage from 'pages/settings/ai-usage/index'
import SettingsApiPage from 'pages/settings/api/index'
import SettingsBusinessActivitiesPage from 'pages/settings/business-activities/index'
import SettingsBusinessProcessesPage from 'pages/settings/business-processes/index'
import SettingsBusinessUnitsPage from 'pages/settings/business-units/index'
import SettingsDataPrivacyPage from 'pages/settings/data-privacy/index'
import SettingsIntegrationsPage from 'pages/settings/integrations/index'
import SettingsLocationsPage from 'pages/settings/locations/index'
import SettingsObjectsPage from 'pages/settings/objects/index'
import SettingsObjectsEditPage from 'pages/settings/objects/[id]'
import SettingsPlanTypesPage from 'pages/settings/plan-types/index'
import SettingsPlanTypesEditPage from 'pages/settings/plan-types/[id]'
import SettingsPositionsPage from 'pages/settings/positions/index'
import SettingsPowerBiPage from 'pages/settings/power-bi/index'
import SettingsSecurityPage from 'pages/settings/security/index'
import SettingsSystemProcessesPage from 'pages/settings/system-processes/index'
import SettingsUsersPage from 'pages/settings/users/index'
import SettingsUsersEditPage from 'pages/settings/users/[id]'
// other
import NotFoundPage from 'pages/not-found/index'

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path='/' element={<LoginPage />} />
      <Route path='/login' element={<LoginPage />} />
      <Route path='/login/verify-2fa' element={<Verify2FA />} />
      <Route path='/azuresso' element={<AzureSSO />} />
      <Route path='/reset-password' element={<ResetPassword />} />
      <Route path='/reset-password/confirm' element={<ResetPasswordConfirm />} />
      <Route path='/public/form/:id' element={<PublicFormPage />} />
      <Route path='/public/form/create/:id' element={<PublicFormCreatePage />} />

      <Route element={<ProtectedRoute />}>
        <Route path='/callback' element={<Callback />} />
        <Route path='/home' element={<HomePage />} />
        <Route path='/account/security' element={<AccountSecurity />} />

        <Route path='/plans'>
          <Route path=':companyId/edit/:id' element={<PlanEditPage />} />
          <Route path='*' element={<PlansPage />} />
        </Route>

        <Route path='/kpis/*' element={<KpisPage />} />
        <Route path='/actions/*' element={<ActionsPage />} />
        <Route path='/objectives/*' element={<ObjectivesPage />} />

        <Route path='/forms'>
          <Route index element={<FormsPage />} />
          <Route path='create/:id' element={<FormsCreatePage />} />
          <Route path='templates/edit/:id' element={<FormsEditMultiplePage />} />
          <Route path='edit/template/:templateId' element={<FormsEditTemplatePage />} />
          <Route path='edit/:id/template/:templateId' element={<FormsEditPage />} />
        </Route>

        <Route path='/data-hub'>
          <Route index element={<DataHubPage />} />
          <Route path='report/:id/workspace/:workspaceId/name/:name' element={<DataHubReportPage />} />
          <Route path='report/edit/:id/workspace/:workspaceId/name/:name' element={<DataHubReportEditPage />} />
          <Route path='report/new/dataset/:datasetId/workspace/:workspaceId' element={<DataHubReportNewPage />} />
        </Route>

        <Route path='/settings'>
          <Route path='accounts/new' element={<SettingsAccountsNewPage />} />
          <Route path='accounts/:id' element={<SettingsAccountsEditPage />} />
          <Route path='accounts' element={<SettingsAccountsPage />} />
          <Route path='ai-usage' element={<SettingsAiUsagePage />} />
          <Route path='api' element={<SettingsApiPage />} />
          <Route path='business-activities' element={<SettingsBusinessActivitiesPage />} />
          <Route path='business-processes' element={<SettingsBusinessProcessesPage />} />
          <Route path='business-units' element={<SettingsBusinessUnitsPage />} />
          <Route path='data-privacy' element={<SettingsDataPrivacyPage />} />
          <Route path='integrations' element={<SettingsIntegrationsPage />} />
          <Route path='locations' element={<SettingsLocationsPage />} />
          <Route path='objects/:id' element={<SettingsObjectsEditPage />} />
          <Route path='objects' element={<SettingsObjectsPage />} />
          <Route path='plan-types/:id' element={<SettingsPlanTypesEditPage />} />
          <Route path='plan-types' element={<SettingsPlanTypesPage />} />
          <Route path='positions' element={<SettingsPositionsPage />} />
          <Route path='power-bi' element={<SettingsPowerBiPage />} />
          <Route path='security' element={<SettingsSecurityPage />} />
          <Route path='system-processes' element={<SettingsSystemProcessesPage />} />
          <Route path='users/:id' element={<SettingsUsersEditPage />} />
          <Route path='users' element={<SettingsUsersPage />} />
        </Route>
      </Route>

      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  )
}

export default AppRoutes
